<script setup lang="ts">
type Props = {
  userCanChangeCountry?: boolean;
  showLabel?: boolean;
  withCheck?: boolean;
};

const {
  userCanChangeCountry = false,
  showLabel = false,
  withCheck = false,
} = defineProps<Props>();

const { allCountriesList } = storeToRefs(useCountriesStore());
const { availableCountries } = useParameters();

const { visitorOrUserCountry } = useParameters();

const countriesSortCountry = computed(() => {
  if (!allCountriesList?.value) {
    return;
  }

  return allCountriesList.value
    .filter((country: any) => {
      return availableCountries.value.includes(country.code);
    })
    .sort((a: any, b: any) => a.code.localeCompare(b.code));
});

const selectedCountry = ref<string>('');

function fillDefaultSelectedCountryValue() {
  if (!visitorOrUserCountry?.value) {
    return;
  }

  selectedCountry.value = visitorOrUserCountry.value;
}

onMounted(() => {
  fillDefaultSelectedCountryValue();
});

onActivated(() => {
  fillDefaultSelectedCountryValue();
});

watch(
  () => allCountriesList.value,
  () => {
    fillDefaultSelectedCountryValue();
  },
);

defineExpose({
  selectedCountry,
  availableCountries,
});
</script>

<template>
  <ClientOnly>
    <div :class="$style.formGroup">
      <label
        v-if="showLabel"
        for="userCountry"
      >
        {{ $t('account.user_data.country') }}
      </label>

      <div
        :class="[
          $style.editableField,
          { [$style.isDisabled]: !userCanChangeCountry },
        ]"
      >
        <HeadlessListbox
          v-model="selectedCountry"
          as="div"
          :class="$style.listBoxContainer"
          :disabled="!userCanChangeCountry"
        >
          <HeadlessListboxButton
            id="userCountry"
            :class="$style.listBoxButton"
          >
            <NuxtIcon
              v-if="selectedCountry"
              :class="$style.flagIcon"
              :filled="true"
              :name="`flags/${selectedCountry}`"
            />
            {{ selectedCountry || $t('account.user_data.country') }}
            <NuxtIcon
              v-if="userCanChangeCountry"
              :class="$style.arrowIcon"
              name="arrows/angle_down"
            />
            <div
              v-else-if="withCheck"
              :class="[$style.arrowIcon, $style.checkIcon]"
            >
              <NuxtIcon name="check" />
            </div>
          </HeadlessListboxButton>
          <HeadlessListboxOptions
            v-if="userCanChangeCountry"
            class="absolute mt-3 min-w-32 w-full origin-top-right divide-y rounded-md bg-auth-bg-inputs shadow-lg ring-1 ring-black/5 focus:outline-none z-[999] overflow-auto max-h-[205px] p-1"
          >
            <HeadlessListboxOption
              v-for="option in countriesSortCountry"
              :key="option.id"
              :value="option.code"
            >
              <span :class="$style.optionItem">
                <NuxtIcon
                  class="text-xl mr-2"
                  :filled="true"
                  :name="`flags/${option.code}`"
                />
                {{ option.code }}
              </span>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </HeadlessListbox>
      </div>
    </div>
  </ClientOnly>
</template>

<style module lang="postcss">
.formGroup {
  @apply relative w-full;

  label {
    @apply text-xs font-medium;
  }

  .listBoxContainer {
    @apply relative inline-block text-left w-full;

    .listBoxButton {
      @apply inline-flex w-full items-center justify-between gap-1 rounded-none px-4 py-2 text-sm font-medium disabled:cursor-not-allowed text-auth-text-inputs;

      .flagIcon {
        @apply text-xl mr-2;
      }

      .arrowIcon {
        @apply ml-auto opacity-30;

        &.checkIcon {
          @apply opacity-100 text-success fill-success;
        }
      }
    }

    .optionItem {
      @apply flex w-full items-center gap-1 rounded-md px-2 py-2 text-sm font-medium cursor-pointer text-auth-text-inputs;
    }
  }

  .editableField {
    @apply bg-auth-bg-inputs h-[46px] sm:h-[50px] rounded-md flex items-center relative;

    &.isDisabled {
      @apply cursor-not-allowed opacity-50;

      &:hover {
        @apply opacity-50;
      }

      button,
      label,
      div {
        @apply cursor-not-allowed;
        &:hover {
          @apply opacity-100;
        }
      }
    }
  }
}
</style>
